import { ref, get, set, update, remove } from "firebase/database";
import { db } from "../firebaseConfig";

export interface AuthorizedUser {
	email: string;
	displayName: string;
	role: "admin" | "user";
	createdAt: string;
	isActive: boolean;
	isPartner?: boolean;
}

/**
 * Get all authorized users from the database
 */
export const getAuthorizedUsers = async (): Promise<
	Record<string, AuthorizedUser>
> => {
	try {
		const usersRef = ref(db, "authorizedUsers");
		const snapshot = await get(usersRef);
		if (snapshot.exists()) {
			return snapshot.val();
		}
		return {};
	} catch (error) {
		console.error("Error fetching authorized users:", error);
		return {};
	}
};

/**
 * Check if a user is authorized by email
 */
export const isUserAuthorized = async (email: string): Promise<boolean> => {
	try {
		const usersRef = ref(db, `authorizedUsers`);
		const snapshot = await get(usersRef);
		if (snapshot.exists()) {
			const users = snapshot.val();
			const authorizedUser = Object.values(users).find(
				(user: any) => user.email === email && user.isActive === true
			);
			return !!authorizedUser;
		}
		return false;
	} catch (error) {
		console.error("Error checking user authorization:", error);
		return false;
	}
};

/**
 * Add an authorized user to the database
 */
export const addAuthorizedUser = async (
	user: AuthorizedUser
): Promise<boolean> => {
	try {
		const usersRef = ref(
			db,
			`authorizedUsers/${user.email.replace(/\./g, "_")}`
		);
		await set(usersRef, user);
		return true;
	} catch (error) {
		console.error("Error adding authorized user:", error);
		return false;
	}
};

/**
 * Update an authorized user
 */
export const updateAuthorizedUser = async (
	email: string,
	updates: Partial<AuthorizedUser>
): Promise<boolean> => {
	try {
		const userRef = ref(db, `authorizedUsers/${email.replace(/\./g, "_")}`);
		await update(userRef, updates);
		return true;
	} catch (error) {
		console.error("Error updating authorized user:", error);
		return false;
	}
};

/**
 * Deactivate an authorized user
 */
export const deactivateUser = async (email: string): Promise<boolean> => {
	return updateAuthorizedUser(email, { isActive: false });
};

/**
 * Remove an authorized user from the database
 */
export const removeAuthorizedUser = async (email: string): Promise<boolean> => {
	try {
		const userRef = ref(db, `authorizedUsers/${email.replace(/\./g, "_")}`);
		await remove(userRef);
		return true;
	} catch (error) {
		console.error("Error removing authorized user:", error);
		return false;
	}
};

/**
 * Get user role (admin or user)
 */
export const getUserRole = async (
	email: string
): Promise<"admin" | "user" | null> => {
	try {
		const userRef = ref(db, `authorizedUsers/${email.replace(/\./g, "_")}`);
		const snapshot = await get(userRef);
		if (snapshot.exists()) {
			const user = snapshot.val() as AuthorizedUser;
			if (user.isActive) {
				return user.role;
			}
		}
		return null;
	} catch (error) {
		console.error("Error fetching user role:", error);
		return null;
	}
};
